import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Empty,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import moment from 'moment';
import { getHistorialIntentosTimbrado, indicadorHistorialIntentos } from 'api/nominas/timbrado';
import { DATE_FRONTEND_FORMAT } from 'utils/normalizers';

const { Text } = Typography;

const SIN_DATO = '—';
const FORMATO_FECHA_HORA = `${DATE_FRONTEND_FORMAT} HH:mm:ss`;
export const TEXTO_DESCRIPCION_OMITIDA = 'Omitida: podría contener datos personales';

const formatearFechaHora = (valor) => {
  if (!valor) return SIN_DATO;
  const fecha = moment(valor);
  return fecha.isValid() ? fecha.format(FORMATO_FECHA_HORA) : SIN_DATO;
};

const columnas = [
  {
    title: 'Intento',
    dataIndex: 'numeroDeIntento',
    key: 'numeroDeIntento',
    width: 90,
    render: (valor, intento) => (
      <>
        {valor ?? SIN_DATO}
        {intento.esUltimoIntento && <Tag color="blue" style={{ marginLeft: 6 }}>Último</Tag>}
      </>
    ),
  },
  {
    title: 'Fecha de Envío',
    dataIndex: 'fechaEnvio',
    key: 'fechaEnvio',
    render: formatearFechaHora,
  },
  {
    title: 'Fecha de Respuesta',
    dataIndex: 'fechaRespuesta',
    key: 'fechaRespuesta',
    render: formatearFechaHora,
  },
  {
    title: 'Estado',
    dataIndex: 'estadoDescripcion',
    key: 'estadoDescripcion',
    render: (valor) => valor || SIN_DATO,
  },
  {
    title: 'Código',
    dataIndex: 'codigo',
    key: 'codigo',
    render: (valor) => valor ?? SIN_DATO,
  },
  {
    title: 'Descripción',
    dataIndex: 'descripcion',
    key: 'descripcion',
    // Solo texto: React escapa el contenido; nunca interpretarlo como HTML
    render: (valor, intento) => (intento.descripcionOmitida
      ? <Text type="secondary">{TEXTO_DESCRIPCION_OMITIDA}</Text>
      : valor || SIN_DATO),
  },
  {
    title: 'Violaciones',
    dataIndex: 'violaciones',
    key: 'violaciones',
    render: (valor) => (valor?.length ? valor.join(', ') : SIN_DATO),
  },
  {
    title: 'Descarte',
    key: 'descarte',
    render: (_, intento) => (intento.descartadoEn
      ? `${intento.descartadoPor || SIN_DATO} · ${formatearFechaHora(intento.descartadoEn)}`
      : SIN_DATO),
  },
];

const HistorialIntentosTimbrado = ({ reciboId }) => {
  const [loading, setLoading] = useState(false);
  // reciboId indica a qué recibo pertenece el historial, para no mostrar el de otro
  const [historial, setHistorial] = useState(null);

  useEffect(() => {
    let mounted = true;
    if (!reciboId) {
      setHistorial(null);
      return undefined;
    }
    const fetchHistorial = async () => {
      setLoading(true);
      const respuesta = await getHistorialIntentosTimbrado(reciboId);
      if (mounted) {
        setHistorial({ ...respuesta, reciboId });
        setLoading(false);
      }
    };
    fetchHistorial();
    return () => { mounted = false; };
  }, [reciboId]);

  const listo = !loading && !!historial && historial.reciboId === reciboId;

  const contenido = () => {
    const {
      intentos, total, error, sinPermiso,
    } = historial;
    if (sinPermiso) {
      return (
        <Alert
          type="warning"
          showIcon
          message="No tienes permiso para consultar el historial de intentos de timbrado."
        />
      );
    }
    if (error) {
      return (
        <Alert
          type="error"
          showIcon
          message="No fue posible consultar el historial de intentos de timbrado."
        />
      );
    }
    if (!intentos.length) {
      return <Empty description="Este recibo no tiene intentos de timbrado SystiCloud" />;
    }
    const indicador = indicadorHistorialIntentos(intentos, total);
    return (
      <>
        {indicador && (
          <Tag
            color={indicador.tipo === 'tras_reintento' ? 'green' : 'red'}
            style={{ marginBottom: 12 }}
          >
            {indicador.texto}
          </Tag>
        )}
        {total > intentos.length && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Se muestran los primeros ${intentos.length} de ${total} intentos.`}
          />
        )}
        <Table
          rowKey="id"
          size="small"
          columns={columnas}
          dataSource={intentos}
          pagination={false}
          scroll={{ x: 900 }}
        />
      </>
    );
  };

  return (
    <Spin tip="Cargando..." spinning={loading}>
      {listo ? contenido() : <div style={{ minHeight: 80 }} />}
    </Spin>
  );
};

HistorialIntentosTimbrado.propTypes = {
  reciboId: PropTypes.number,
};

HistorialIntentosTimbrado.defaultProps = {
  reciboId: null,
};

export default HistorialIntentosTimbrado;
